"use client";

import { c as _c } from "react/compiler-runtime";
import { addYears, format, isSameMonth, setMonth, setYear, startOfMonth } from "date-fns";
import * as React from "react";
import { DayPicker, type DayPickerRangeProps, type DayPickerSingleProps, type DayProps, type Matcher, useDayPicker, useDayRender, useNavigation } from "react-day-picker";
import { RiArrowDownSLine, RiArrowDropLeftLine, RiArrowDropRightLine, RiArrowLeftDoubleLine, RiArrowLeftSLine, RiArrowRightDoubleLine, RiArrowRightSLine, RiCalendar2Line } from "react-icons/ri";
import { TimezoneScopeContext } from "@/contexts/timezone-scope";
import { clinicClockFor, dayMarker } from "@/lib/clinic-clock";
import { cx, focusRing } from "@/lib/tailwind-utils";
import { useStoreTimezone } from "@/lib/timezone";
import Button from "./button";

// Tremor Raw Calendar [v0.0.2]

interface NavigationButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
  onClick: () => void;
  icon: React.ElementType<{
    className?: string;
  }>;
  disabled?: boolean;
}
const NavigationButton = React.forwardRef<HTMLButtonElement, NavigationButtonProps>((t0, forwardedRef) => {
  const $ = _c(11);
  let disabled;
  let icon;
  let onClick;
  let props;
  if ($[0] !== t0) {
    ({
      onClick,
      icon,
      disabled,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = disabled;
    $[2] = icon;
    $[3] = onClick;
    $[4] = props;
  } else {
    disabled = $[1];
    icon = $[2];
    onClick = $[3];
    props = $[4];
  }
  let t1;
  if ($[5] !== disabled || $[6] !== forwardedRef || $[7] !== icon || $[8] !== onClick || $[9] !== props) {
    t1 = <Button variant="secondary" type="button" size="icon-sm" ref={forwardedRef} disabled={disabled} onClick={onClick} icon={icon} {...props} />;
    $[5] = disabled;
    $[6] = forwardedRef;
    $[7] = icon;
    $[8] = onClick;
    $[9] = props;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  return t1;
});
NavigationButton.displayName = "NavigationButton";
type OmitKeys<T, K extends keyof T> = { [P in keyof T as P extends K ? never : P]: T[P] };
type KeysToOmit = "showWeekNumber" | "captionLayout" | "mode";
type SingleProps = OmitKeys<DayPickerSingleProps, KeysToOmit>;
type RangeProps = OmitKeys<DayPickerRangeProps, KeysToOmit>;
type CalendarProps = ({
  mode: "single";
} & SingleProps) | ({
  mode?: undefined;
} & SingleProps) | ({
  mode: "range";
} & RangeProps);
const Calendar = ({
  mode = "single",
  weekStartsOn = 0,
  numberOfMonths = 1,
  enableYearNavigation = false,
  enableDecadeNavigation = false,
  enableMonthYearDropdowns = false,
  disableNavigation,
  locale,
  className,
  classNames,
  components,
  enableTodayButton = false,
  loading = false,
  loadingClassName = "",
  ...props
}: CalendarProps & {
  enableYearNavigation?: boolean;
  enableDecadeNavigation?: boolean;
  // Replaces the month label with month and year selects. Native selects open
  // as a scroll wheel on iPad, so a birth year is one swipe away.
  enableMonthYearDropdowns?: boolean;
  enableTodayButton?: boolean;
  loading?: boolean;
  loadingClassName?: string;
}) => {
  // Today is the enclosing scope's location day, else the viewer's. Subscribed
  // to the timezone store so it moves when the zone does; this primitive
  // renders outside the console providers too.
  const scopedTimeZone = React.useContext(TimezoneScopeContext);
  const storeTimeZone = useStoreTimezone();
  const todayMarker = dayMarker(clinicClockFor(scopedTimeZone ?? storeTimeZone).today());
  return <DayPicker mode={mode} weekStartsOn={weekStartsOn} numberOfMonths={numberOfMonths} locale={locale} showOutsideDays={numberOfMonths === 1 ? true : false} classNames={{
    months: "space-y-0",
    month: "space-y-4 p-3",
    nav: "gap-1 flex items-center rounded-full size-full justify-between p-4",
    table: cx("relative w-full border-collapse space-y-1", loading && `after:absolute after:inset-0 after:z-20 after:text-body after:font-medium after:flex after:items-center after:justify-center ${loadingClassName}`),
    head_cell: "w-9 text-body-strong sm:text-caption-strong text-center text-foreground-muted pb-2",
    row: "w-full mt-0.5",
    cell: cx("relative p-0 text-center focus-within:relative", "text-foreground"),
    day: cx("size-9 rounded text-body text-foreground", "hover:bg-hover", focusRing),
    day_today: "font-semibold",
    day_selected: cx("rounded", "aria-selected:bg-primary aria-selected:text-white"),
    day_disabled: "!text-foreground-muted line-through disabled:hover:bg-transparent",
    day_outside: "text-foreground-muted",
    day_range_middle: cx("!rounded-none", "aria-selected:!bg-hover aria-selected:!text-foreground"),
    day_range_start: "rounded-r-none !rounded-l",
    day_range_end: "rounded-l-none !rounded-r",
    day_hidden: "invisible",
    ...classNames
  }} className={cx(className)} components={{
    IconLeft: () => <RiArrowLeftSLine aria-hidden="true" className="size-4" />,
    IconRight: () => <RiArrowRightSLine aria-hidden="true" className="size-4" />,
    Caption: ({
      ...props_0
    }) => {
      const {
        goToMonth,
        nextMonth,
        previousMonth,
        currentMonth,
        displayMonths,
        goToDate
      } = useNavigation();
      const {
        numberOfMonths: numberOfMonths_0,
        fromDate,
        toDate,
        today
      } = useDayPicker();
      const displayIndex = displayMonths.findIndex(month => isSameMonth(props_0.displayMonth, month));
      const isFirst = displayIndex === 0;
      const isLast = displayIndex === displayMonths.length - 1;
      const hideNextButton = numberOfMonths_0 > 1 && (isFirst || !isLast);
      const hidePreviousButton = numberOfMonths_0 > 1 && (isLast || !isFirst);
      const goToPreviousYear = () => {
        const targetMonth = addYears(currentMonth, -1);
        if (previousMonth && (!fromDate || targetMonth.getTime() >= fromDate.getTime())) {
          goToMonth(targetMonth);
        }
      };
      const goToNextYear = () => {
        const targetMonth_0 = addYears(currentMonth, 1);
        if (nextMonth && (!toDate || targetMonth_0.getTime() <= toDate.getTime())) {
          goToMonth(targetMonth_0);
        }
      };
      const goToPreviousDecade = () => {
        const targetMonth_1 = addYears(currentMonth, -10);
        if (previousMonth && (!fromDate || targetMonth_1.getTime() >= fromDate.getTime())) {
          goToMonth(targetMonth_1);
        }
      };
      const goToNextDecade = () => {
        const targetMonth_2 = addYears(currentMonth, 10);
        if (nextMonth && (!toDate || targetMonth_2.getTime() <= toDate.getTime())) {
          goToMonth(targetMonth_2);
        }
      };
      const goToToday = () => {
        goToDate(today);
      };

      // Months outside fromDate/toDate have no selectable days, so land on
      // the nearest month that does.
      const goToClampedMonth = (target: Date) => {
        if (toDate && target > toDate) {
          goToMonth(startOfMonth(toDate));
        } else if (fromDate && target < startOfMonth(fromDate)) {
          goToMonth(startOfMonth(fromDate));
        } else {
          goToMonth(target);
        }
      };
      const currentYear = today.getFullYear();
      const firstYear = fromDate?.getFullYear() ?? currentYear - 120;
      const lastYear = toDate?.getFullYear() ?? currentYear + 20;
      const years = Array.from({
        length: lastYear - firstYear + 1
      }, (_, i) => lastYear - i);
      // Each select is invisible and stretched over text styled like the
      // plain month label, so the header keeps its look while a tap still
      // opens the native picker.
      const captionSelect = (text: string, select: React.ReactElement) => <label className={cx("relative flex cursor-pointer items-center gap-0.5 rounded px-1.5 py-1 text-body-strong tabular-nums text-foreground hover:bg-hover", "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary/70")}>
              <span aria-hidden="true">{text}</span>
              <RiArrowDownSLine aria-hidden="true" className="size-4 text-foreground-muted" />
              {select}
            </label>;
      const selectClassName = "absolute inset-0 cursor-pointer opacity-0 disabled:cursor-default";
      return <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                {enableDecadeNavigation && !hidePreviousButton && <NavigationButton disabled={disableNavigation || !previousMonth || fromDate && addYears(currentMonth, -10).getTime() < fromDate.getTime()} aria-label="Go to previous decade" onClick={goToPreviousDecade} icon={RiArrowLeftDoubleLine} />}
                {enableYearNavigation && !hidePreviousButton && <NavigationButton disabled={disableNavigation || !previousMonth || fromDate && addYears(currentMonth, -1).getTime() < fromDate.getTime()} aria-label="Go to previous year" onClick={goToPreviousYear} icon={enableDecadeNavigation ? RiArrowLeftSLine : RiArrowLeftDoubleLine} />}
                {!hidePreviousButton && <NavigationButton disabled={disableNavigation || !previousMonth} aria-label="Go to previous month" onClick={() => previousMonth && goToMonth(previousMonth)} icon={enableDecadeNavigation ? RiArrowDropLeftLine : RiArrowLeftSLine} />}
              </div>

              {enableMonthYearDropdowns ? <div className="flex items-center">
                  {captionSelect(format(props_0.displayMonth, "LLLL", {
            locale
          }), <select aria-label="Month" className={selectClassName} disabled={disableNavigation} value={props_0.displayMonth.getMonth()} onChange={e => goToClampedMonth(setMonth(props_0.displayMonth, Number(e.target.value)))}>
                      {Array.from({
              length: 12
            }, (__0, i_0) => <option key={i_0} value={i_0}>
                          {format(new Date(2000, i_0, 1), "LLLL", {
                locale
              })}
                        </option>)}
                    </select>)}
                  {captionSelect(format(props_0.displayMonth, "yyyy", {
            locale
          }), <select aria-label="Year" className={selectClassName} disabled={disableNavigation} value={props_0.displayMonth.getFullYear()} onChange={e_0 => goToClampedMonth(setYear(props_0.displayMonth, Number(e_0.target.value)))}>
                      {years.map(year => <option key={year} value={year}>
                          {year}
                        </option>)}
                    </select>)}
                </div> : <div role="presentation" aria-live="polite" className="px-2 text-body-strong capitalize tabular-nums text-foreground">
                  {format(props_0.displayMonth, "LLLL yyy", {
            locale
          })}
                </div>}

              <div className="flex items-center gap-1">
                {!hideNextButton && <NavigationButton disabled={disableNavigation || !nextMonth} aria-label="Go to next month" onClick={() => nextMonth && goToMonth(nextMonth)} icon={enableDecadeNavigation ? RiArrowDropRightLine : RiArrowRightSLine} />}
                {enableYearNavigation && !hideNextButton && <NavigationButton disabled={disableNavigation || !nextMonth || toDate && addYears(currentMonth, 1).getTime() > toDate.getTime()} aria-label="Go to next year" onClick={goToNextYear} icon={enableDecadeNavigation ? RiArrowRightSLine : RiArrowRightDoubleLine} />}
                {enableDecadeNavigation && !hideNextButton && <NavigationButton disabled={disableNavigation || !nextMonth || toDate && addYears(currentMonth, 10).getTime() > toDate.getTime()} aria-label="Go to next decade" onClick={goToNextDecade} icon={RiArrowRightDoubleLine} />}

                {enableTodayButton && <NavigationButton aria-label="Go to today" onClick={goToToday} icon={RiCalendar2Line} />}
              </div>
            </div>;
    },
    Day: ({
      date,
      displayMonth
    }: DayProps) => {
      const buttonRef = React.useRef<HTMLButtonElement | null>(null);
      const {
        activeModifiers,
        buttonProps,
        divProps,
        isButton,
        isHidden
      } = useDayRender(date, displayMonth, buttonRef as React.RefObject<HTMLButtonElement>);
      const {
        selected,
        today: today_0,
        disabled,
        range_middle
      } = activeModifiers;
      if (isHidden) {
        return <></>;
      }
      if (!isButton) {
        return <div {...divProps} className={cx("flex items-center justify-center", divProps.className)} />;
      }
      const {
        children: buttonChildren,
        className: buttonClassName,
        ...buttonPropsRest
      } = buttonProps;
      return <button ref={buttonRef} {...buttonPropsRest} type="button" className={cx("relative", buttonClassName)}>
              {buttonChildren}
              {today_0 && <span className={cx("absolute inset-x-1/2 bottom-1.5 h-0.5 w-4 -translate-x-1/2 rounded-[2px]", {
          "bg-primary": !selected,
          "!": selected,
          "!bg-foreground-muted": selected && range_middle,
          "bg-foreground-muted text-foreground-muted": disabled
        })} />}
            </button>;
    },
    // Callers override single pieces (e.g. Caption) and keep the rest.
    ...components
  }}
  // "Today" is the clinic's day, as the picker's browser-local marker.
  today={todayMarker} {...props as SingleProps & RangeProps} />;
};
Calendar.displayName = "Calendar";
export { Calendar, type Matcher };